import React, { useEffect, useState } from 'react';
import './ChampionSkillOrder.css';
import DataService, { SpellImage } from '../../../XXX/DataService';

interface SkillOrderDetail {
  order_string: string;
  games: number;
  win_rate_pct: number;
  extended_order?: string;
}

interface ChampionSkillOrderProps {
  championName: string;
}

// Parse skill order string into a structured format
const parseSkillOrder = (orderString?: string): number[][] | null => {
  if (!orderString) {
    console.warn('No order string provided to parseSkillOrder');
    return null;
  }
  
  const skills = orderString.toString().split('-').map(s => parseInt(s, 10));
  
  // Create a 4x18 grid (Q,W,E,R x 18 levels)
  const grid: number[][] = [
    Array(18).fill(0), // Q - index 0
    Array(18).fill(0), // W - index 1
    Array(18).fill(0), // E - index 2
    Array(18).fill(0)  // R - index 3
  ];
  
  // Fill in the grid with level numbers
  skills.forEach((skill, levelIndex) => {
    // Only process up to 18 levels
    if (levelIndex < 18) {
      // Convert from 1-based to 0-based indexing
      const skillIndex = skill - 1;
      if (skillIndex >= 0 && skillIndex < 4) {
        // Mark the level (levelIndex+1) where this skill is taken
        grid[skillIndex][levelIndex] = levelIndex + 1;
      }
    }
  });
  
  return grid;
};

const ChampionSkillOrder: React.FC<ChampionSkillOrderProps> = ({ championName }) => {
  const [loading, setLoading] = useState(true);
  const [skillOrder, setSkillOrder] = useState<SkillOrderDetail[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [spellImages, setSpellImages] = useState<SpellImage[]>([]);
  const [spellImagesLoading, setSpellImagesLoading] = useState(true);

  useEffect(() => {
    const fetchSpellImages = async () => {
      if (!championName) return;
      
      try {
        setSpellImagesLoading(true);
        const championData = await DataService.fetchChampionHeaderData(championName);
        setSpellImages(championData.spells);
      } catch (err) {
        console.error('Error fetching spell images:', err);
      } finally {
        setSpellImagesLoading(false);
      }
    };

    const fetchSkillData = async () => {
      if (!championName) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Fetch skill order data - add parameter to get extended data if available
        const orderResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/api/stats/champion/${championName}/skillOrder?levels=18`);
        if (!orderResponse.ok) {
          throw new Error(`Failed to fetch skill order data: ${orderResponse.status}`);
        }
        const orderData = await orderResponse.json();
        
        // Check structure of received skill order data
        if (orderData && orderData.length > 0) {
          const firstItem = orderData[0];
          
          // Check which property contains the order string
          if (firstItem.sequence && !firstItem.order_string) {
            // Map data to correct format if needed
            const mappedOrderData = orderData.map((item: any) => ({
              order_string: item.sequence,
              games: item.count || 0,
              win_rate_pct: item.win_rate || 0,
              extended_order: item.extended_sequence || item.sequence // Use extended sequence if available
            }));
            setSkillOrder(mappedOrderData);
          } else if (firstItem.order_string) {
            setSkillOrder(orderData);
          } else {
            throw new Error('Unexpected skill order data format');
          }
        } else {
          setSkillOrder([]);
        }
        
      } catch (err) {
        console.error('Error fetching skill data:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSkillData();
    fetchSpellImages();
  }, [championName]);
  
  // Helper function to render spell image with key label
  const renderSpellImage = (index: number, keyLabel: string) => {
    if (spellImagesLoading) {
      return <div className="so-spell-loading">{keyLabel}</div>;
    }
    
    if (!spellImages || spellImages.length <= index) {
      return keyLabel;
    }
    
    const spell = spellImages[index];
    return (
      <div className="so-spell-image-container">
        <img 
          src={spell.imagePath} 
          alt={`${championName} ${keyLabel}`} 
          className="so-spell-image" 
        />
        <span className="so-spell-key">{keyLabel}</span>
      </div>
    );
  };
  
  // If loading, show appropriate message
  if (loading) {
    return <div className="champion-skill-order so-loading">Loading skill data...</div>;
  }
  
  // If error, show error message
  if (error) {
    return <div className="champion-skill-order so-empty">Error loading skill data: {error}</div>;
  }
  
  // If no data, show appropriate message
  if (!skillOrder?.length) {
    return <div className="champion-skill-order so-empty">No skill order data available for {championName}</div>;
  }
  
  const topOrder = skillOrder[0];
  
  // Parse the skill order into a grid format
  const skillGrid = topOrder && parseSkillOrder(topOrder.extended_order || topOrder.order_string);
  
  // Define labels for abilities
  const abilityLabels = ['Q', 'W', 'E', 'R'];
  
  return (
    <div className="champion-skill-order">
      <div className="so-skill-order-container">    
        
        {/* Skill Order Grid */}
        {skillGrid && (
          <div className="so-skill-order-grid">
            {/* Skill rows (Q, W, E, R) */}
            {skillGrid.map((row, skillIndex) => (
              <div key={skillIndex} className="so-skill-grid-row">
                {/* Ability icon cell */}
                <div className="so-skill-cell so-ability-cell">
                  {renderSpellImage(skillIndex, abilityLabels[skillIndex])}
                </div>
                
                {/* Level cells - show all 18 */}
                {row.map((levelNum, levelIndex) => (
                  <div 
                    key={levelIndex} 
                    className={`so-skill-cell ${levelNum > 0 ? 'so-filled' : ''}`}
                  >
                    {levelNum > 0 ? levelNum : ''}
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChampionSkillOrder; 